// src/components/policies/LifeInsuranceCard.jsx
// Displays a single life insurance policy

import React, { useState } from 'react';
import {
  Heart, ChevronDown, ChevronUp, Edit2, Trash2, DollarSign,
  Calendar, User, Shield, Loader2
} from 'lucide-react';
import { deleteLifeInsurancePolicy } from '../../api';
import AddLifeInsuranceModal from './AddLifeInsuranceModal';

const POLICY_TYPE_LABELS = {
  final_expense: 'Final Expense',
  term: 'Term Life',
  universal_life: 'Universal Life',
  whole_life: 'Whole Life'
};

const STATUS_STYLES = {
  active: 'bg-green-100 text-green-700',
  lapsed: 'bg-red-100 text-red-700',
  surrendered: 'bg-orange-100 text-orange-700',
  paid_up: 'bg-blue-100 text-blue-700'
};

export default function LifeInsuranceCard({ policy, clientId, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this policy?')) return;

    try {
      setDeleting(true);
      await deleteLifeInsurancePolicy(clientId, policy.id);
      onUpdate?.();
    } catch (err) {
      console.error('[LifeInsuranceCard] Delete error:', err);
      alert('Failed to delete policy');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="border border-slate-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow">
        {/* Header */}
        <div
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition-colors"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-rose-100 flex items-center justify-center">
              <Heart className="w-5 h-5 text-rose-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-medium text-slate-800">{policy.carrier}</h4>
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_STYLES[policy.status] || 'bg-slate-100 text-slate-600'}`}>
                  {policy.status}
                </span>
              </div>
              <p className="text-sm text-slate-500">
                {POLICY_TYPE_LABELS[policy.policyType] || policy.policyType}
                {policy.policyNumber && ` • ${policy.policyNumber}`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="font-semibold text-slate-800">{formatCurrency(policy.faceAmount)}</p>
              <p className="text-xs text-slate-500">{formatCurrency(policy.monthlyPremium)}/mo</p>
            </div>
            {expanded ? (
              <ChevronUp className="w-5 h-5 text-slate-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-400" />
            )}
          </div>
        </div>

        {/* Expanded Details */}
        {expanded && (
          <div className="border-t border-slate-100 p-4 bg-slate-50">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
              <div>
                <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1">
                  <DollarSign className="w-3.5 h-3.5" />
                  Face Amount
                </div>
                <p className="font-medium text-slate-800">{formatCurrency(policy.faceAmount)}</p>
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1">
                  <DollarSign className="w-3.5 h-3.5" />
                  Premium
                </div>
                <p className="font-medium text-slate-800">{formatCurrency(policy.monthlyPremium)}/mo</p>
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1">
                  <Calendar className="w-3.5 h-3.5" />
                  Effective
                </div>
                <p className="font-medium text-slate-800">{formatDate(policy.effectiveDate)}</p>
              </div>
              {policy.termYears && (
                <div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1">
                    <Shield className="w-3.5 h-3.5" />
                    Term
                  </div>
                  <p className="font-medium text-slate-800">{policy.termYears} years</p>
                </div>
              )}
            </div>

            {/* Beneficiary */}
            {policy.beneficiaryName && (
              <div className="mb-4 p-3 bg-white rounded-lg border border-slate-200">
                <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1">
                  <User className="w-3.5 h-3.5" />
                  Beneficiary
                </div>
                <p className="font-medium text-slate-800">
                  {policy.beneficiaryName}
                  {policy.beneficiaryRelationship && (
                    <span className="text-slate-500 font-normal"> ({policy.beneficiaryRelationship})</span>
                  )}
                </p>
              </div>
            )}

            {/* Notes */}
            {policy.notes && (
              <div className="mb-4 p-3 bg-white rounded-lg border border-slate-200">
                <p className="text-sm text-slate-600">{policy.notes}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setShowEdit(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <Edit2 className="w-4 h-4" />
                Edit
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
              >
                {deleting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                Delete
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <AddLifeInsuranceModal
        isOpen={showEdit}
        onClose={() => setShowEdit(false)}
        clientId={clientId}
        policy={policy}
        onSuccess={() => {
          setShowEdit(false);
          onUpdate?.();
        }}
      />
    </>
  );
}
